import clsx from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import {
    accountNamingErrorMessageAtom,
    accountsAtom,
    accountApplicationAtom,
    labelingContextViewAtom,
    selectedRowAtom,
} from "@/features/Labeling/state";
import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { RawInput } from "@/ui";
import { useCallback, useEffect, useState } from "react";
import { AccountLabel } from "@/entities/DataAssociation/Account.model";
import { AccountHintType } from "@/entities/DataAssociation/Draft.model";
import { LabelItem } from "../components/LabelItem";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { Label } from "@/ui/organisms/Form/Label";
import { ACCOUNT_LIST } from "../labeling.constants";
import SearchBar from "@/features/Navigation/components/SearchBar";
import { LabelList } from "../components/LabelList";
import { v7 as uuidv7 } from "uuid";
import { useOnEnter } from "@/ui/hooks/useOnEnter";

export function AccountCreation() {
    const setContextView = useSetAtom(labelingContextViewAtom);
    const setSelectedRow = useSetAtom(selectedRowAtom);
    const setAccountApplication = useSetAtom(accountApplicationAtom);
    const selectedRow = useAtomValue(selectedRowAtom);
    const initial = selectedRow?.account;
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [accountName, setAccountName] = useState<string | null>(
        selectedRow?.rowHeader || null,
    );
    const [selectedLabel, setAccountLabel] = useState<AccountLabel | null>(
        selectedRow?.account?.label ?? null,
    );
    const getErrorMessage = useAtomValue(accountNamingErrorMessageAtom);
    const errorMessage = accountName
        ? getErrorMessage(accountName, {
              currentRowIndex: selectedRow?.rowIndex,
          })
        : null;
    const setAccounts = useSetAtom(accountsAtom);

    useEffect(() => {
        if (selectedRow?.account?.label && selectedRow?.account?.accountName) {
            setAccountLabel(selectedRow?.account?.label);
            setAccountName(selectedRow?.account?.accountName);
        }
    }, [
        selectedRow?.account?.label,
        selectedRow?.account?.accountName,
        setAccountLabel,
        setAccountName,
    ]);

    const handleSubmit = useCallback(() => {
        if (accountName && selectedLabel && !errorMessage && selectedRow) {
            const localAccountId = uuidv7();
            setAccounts({
                id: localAccountId,
                type: AccountHintType.NEW,
                name: accountName,
                label: selectedLabel,
            });
            setAccountApplication({
                rowHeader: selectedRow.rowIndex,
                accountId: localAccountId,
            });
            setSelectedRow(null);
        }
    }, [
        accountName,
        selectedLabel,
        errorMessage,
        selectedRow,
        setAccounts,
        setAccountApplication,
        setSelectedRow,
    ]);

    useOnEnter(handleSubmit, { shiftKey: true });

    if (!selectedRow) return null;

    return (
        <div
            className={clsx(
                "absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className={clsx("mb-3 mt-6 px-6", "flex flex-col", "sticky")}>
                <div className="flex items-start justify-between">
                    <RightSidebarButton
                        onClick={() => setSelectedRow(null)}
                        useEscapeToClose={true}
                    />
                    <button
                        className="quiet -mt-1 p-0"
                        onClick={() =>
                            setContextView({ type: "ACCOUNT_SELECTION_LIST" })
                        }
                    >
                        See existing accounts
                    </button>
                </div>
                <h2 className="pageheading">
                    {selectedRow?.rowHeader || "List of Accounts"}
                </h2>
                <p className="body-s subdued mb-4">
                    Please choose an account name and label to create a new
                    account for this row.
                </p>
                <Label name="name" label="Account Name" />
                <RawInput
                    name="accountName"
                    label="Account Name"
                    showLabel={false}
                    value={accountName || ""}
                    showErrorRing={!!errorMessage}
                    onChange={(e) => setAccountName(e.target.value)}
                />
                {errorMessage && (
                    <p className="ui-element mt-1 text-red-600">
                        {errorMessage}
                    </p>
                )}
            </div>
            <div className="flex flex-col gap-2 px-6">
                <div className="flex items-center justify-between gap-2">
                    <Label name="label" label="Label" />
                    {selectedLabel && selectedLabel !== initial?.label && (
                        <button
                            className="quiet subdued ui-label p-0"
                            onClick={() =>
                                setAccountLabel(initial?.label || null)
                            }
                        >
                            Clear
                            <XCircleIcon className="size-4" />
                        </button>
                    )}
                </div>
                {initial?.label && (
                    <LabelItem
                        dot
                        listItem
                        label={ACCOUNT_LIST[selectedLabel || initial.label]}
                        className={clsx("selected", "mb-2")}
                        recommended={
                            !selectedRow?.hasAppliedAccount &&
                            selectedLabel === initial?.label
                        }
                    />
                )}
                <SearchBar
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search labels..."
                />
            </div>

            <div className="custom-scrollbar overflow-y-auto px-6">
                <LabelList
                    className="mt-0 pb-24"
                    labels={Object.values(ACCOUNT_LIST)
                        .filter((label) => {
                            if (label.code === initial?.label) return false;
                            if (!searchTerm) return true;
                            const term = searchTerm.toLowerCase();
                            return (
                                label.category.toLowerCase().includes(term) ||
                                label.name.toLowerCase().includes(term) ||
                                label.code.toLowerCase().includes(term) ||
                                label.statementType.toLowerCase().includes(term)
                            );
                        })
                        .sort((a, b) => {
                            if (initial?.label) {
                                if (a.code === initial.label) return -1;
                                if (b.code === initial.label) return 1;
                            }
                            return (
                                ACCOUNT_LIST[a.code].category.localeCompare(
                                    ACCOUNT_LIST[b.code].category,
                                ) || a.name.localeCompare(b.name)
                            );
                        })}
                    onLabelSelection={setAccountLabel}
                    selectedLabel={selectedLabel}
                />
            </div>

            <div
                className={clsx(
                    "bg-default w-full p-6",
                    "absolute inset-x-0 bottom-0",
                )}
            >
                <button
                    className="primary xl w-full"
                    disabled={!selectedLabel || !accountName || !!errorMessage}
                    onClick={handleSubmit}
                >
                    Create account
                </button>
            </div>
        </div>
    );
}
